package com.swetdot.swetgram.ui.design

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.ui.graphics.Color
import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow

/**
 * The Swetgram Design System colour tokens, named as the SDS names them.
 *
 * This is the canonical layer; Material3's [ColorScheme] is DERIVED from it by [toColorScheme].
 * Both are provided, because neither alone is enough:
 *
 *  - Material3 has no slot for `text.tertiary`, `border.strong`, `graphite`, `background.deep`,
 *    the pressed accent, or the semantic success/warning/info triple. Nine tokens would have
 *    nowhere to live.
 *  - But `AlertDialog`, `ModalBottomSheet`, `Snackbar` and friends read `MaterialTheme
 *    .colorScheme` internally, and there is no supported way to intercept that. Abandoning the
 *    scheme would mean hand-colouring every one of them.
 *
 * So the tokens are the source of truth and the scheme is a projection of them.
 *
 * THE PALETTE IS STRICTLY MONOCHROME: black, white, and neutral greys only. No state gets a private
 * hue. Emphasis comes from contrast, weight, icon fill, copy, and position. `danger`, `success`, and
 * [liked] remain named tokens because their call sites still express semantic intent even when
 * their current values are neutral.
 *
 * [accent] IS WHITE, AND THAT INVERTS WHO CARRIES THE LABEL. It was a dark red chosen so a white
 * label could sit on it; a white fill needs a BLACK one. `ChatColors.onAccent` is `textInverse`
 * for that reason, and the send disc's arrow, the compose disc's pencil and the primary button's
 * text are all near-black on white now. White on white was the obvious way to get this wrong.
 *
 * The WCAG arithmetic that shaped the previous palette is kept on the record because it is what
 * rules out ever making one red do two jobs. Writing L for relative luminance:
 *
 *  - white label on a red fill, >= 4.5:1, requires  L <= 0.1833
 *  - red as TEXT on the black canvas, >= 4.5:1, requires  L >= 0.1833
 *  - red as TEXT on [card], >= 4.5:1, requires  L >= 0.247
 *
 * The first two meet at one luminance and the third misses the first entirely. The old `#EF4444`
 * is the proof: its own white label measures 3.76:1 and fails.
 *
 * `accentSoft`, `warning` and `info` are GONE rather than neutralised. `info` was a byte-identical
 * second declaration of the brand blue with zero call sites, and would have survived an
 * accent-only edit as the last blue in the app. `warning`'s three sites all print the word beside
 * the colour, so the word already carries the state. `accentSoft`'s only job was Material's three
 * container slots, which now point at [card].
 */
data class SwetgramColors(
    /** `background.deep` -- deepest background ONLY, never a general surface (SDS §4.8). */
    val backgroundDeep: Color,
    val background: Color,
    /** Main surface: sheets, nav bars. */
    val surface: Color,
    /** Cards, secondary containers, and the OTHER person's chat bubble. */
    val card: Color,
    val graphite: Color,
    val border: Color,
    val borderStrong: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    val textTertiary: Color,
    val textInverse: Color,
    /**
     * The emphasis fill: the send disc, the compose disc, the primary button, the badge, the
     * focus ring.
     *
     * WHITE, so whatever sits ON it must be dark -- `ChatColors.onAccent`, which is `textInverse`.
     * It is still called `accent` because it is still the one thing that says "this is the action
     * here"; it simply says it with brightness rather than with hue.
     */
    val accent: Color,
    val accentPressed: Color,
    /**
     * Whatever is drawn ON [accent]: a label, a glyph, a spinner.
     *
     * A TOKEN THAT SHOULD HAVE EXISTED ALL ALONG, and three call sites said so in their own
     * comments before it did. `SwetPrimaryButton`'s label, its loading spinner and
     * `SwetBadgeStyle.Accent`'s number were each a raw `Color.White`, every one of them carrying a
     * note explaining that "`SwetgramColors` has no text-on-accent" and that `ChatColors.onAccent`
     * was the nearest thing but belonged to the chat.
     *
     * That was survivable while the accent was a colour. It stopped being survivable the moment
     * the accent became WHITE: three literals would have painted white on white, and each of them
     * renders, compiles and is simply absent. One token, one place to be right.
     */
    val onAccent: Color,
    /**
     * Presence. White rather than green, because a monochrome palette has no green -- and the dot
     * is still the brightest small thing on a row, which is all it ever had to be.
     */
    val success: Color,
    /**
     * Destruction and failure: Delete, Report, Leave, a failed send, an error banner.
     *
     * MONOCHROME, BY DECISION, AND THE NAME IS KEPT ON PURPOSE. Destruction is carried by the word
     * and by position -- last in the list, under the ordinary entries.
     *
     * It is not folded into [textPrimary] even though the two are equal today, and that is the
     * opposite of the call made on `info`, which WAS deleted for being a duplicate. `info` had
     * zero call sites. This has eighteen, each of them saying "this one is destructive" at the
     * point of use -- which is precisely the record a future decision to re-colour them would
     * have to work from, and which a global replace with `textPrimary` would erase.
     */
    val danger: Color,
    /**
     * The active like tint. It is white in the strict monochrome theme; the inactive tint is
     * secondary grey. State is carried by `SwetIcons.HeartFilled` versus `SwetIcons.Heart` and by
     * the spoken label, never by colour alone.
     */
    val liked: Color,
    val isDark: Boolean,
) {
    /**
     * Projects these tokens onto Material3's slots.
     *
     * `surfaceContainer` is the slot NavigationBar, TopAppBar and ModalBottomSheet actually
     * paint with -- pointing it at `surface` rather than leaving Material's own value is what
     * keeps the chrome on the specified hex instead of a tonally derived approximation.
     */
    fun toColorScheme(): ColorScheme = darkColorScheme().copy(
        primary = accent,
        // Material fills primary controls with white, so their content must use the inverse token.
        onPrimary = onAccent,
        // `card` rather than a tinted accent container. `accentSoft` is gone -- a soft red behind
        // a Material chip is a second red on screen, and the whole point of the fill/text split is
        // that there are exactly two.
        primaryContainer = card,
        onPrimaryContainer = textPrimary,
        // Deliberately the same value: one accent, so nothing can reach for a second.
        tertiary = accent,
        onTertiary = onAccent,
        tertiaryContainer = card,
        secondary = accent,
        onSecondary = onAccent,
        secondaryContainer = card,
        onSecondaryContainer = textPrimary,
        background = background,
        onBackground = textPrimary,
        surface = surface,
        onSurface = textPrimary,
        onSurfaceVariant = textSecondary,
        surfaceVariant = card,
        surfaceContainerLowest = backgroundDeep,
        surfaceContainerLow = background,
        surfaceContainer = surface,
        surfaceContainerHigh = card,
        surfaceContainerHighest = graphite,
        outline = border,
        outlineVariant = border,
        error = danger,
        // `danger` is white, so any filled Material error control needs black content.
        onError = textInverse,
        inverseSurface = textPrimary,
        inverseOnSurface = textInverse,

        // -- THE SEVEN SLOTS THAT WERE STILL MATERIAL'S PURPLE -----------------------------------
        //
        // `darkColorScheme()` binds a COMPLETE baseline before `.copy()` runs, so every slot this
        // projection does not name keeps Material's own value -- and Material's baseline is a
        // purple/pink tonal palette. Setting twenty-eight slots and leaving seven does not make the
        // scheme monochrome; it makes it monochrome in the places somebody thought of.
        //
        // They were found by measuring the built scheme rather than by reading this list, which is
        // the only way they COULD be found: nothing in the source says "and the rest stay purple".
        // `surfaceTint` is the one that actually draws -- Material tints an elevated surface with
        // it -- and it was #D0BCFF, a lilac. This app has already shipped a lilac navigation bar
        // once, with every token in `SwetgramColors` correct.
        //
        // `PaletteContrastTest.everySlotTheProjectionAuthorsIsAGrey` covers the authored slots;
        // these seven are now authored, so they are covered too.
        // Flat, untinted surfaces keep elevation from introducing a white tonal wash.
        surfaceTint = Color.Transparent,
        inversePrimary = accent,
        // An error CONTAINER is a filled surface, and this palette has one vocabulary for those:
        // the neutral card. `danger` is white, so a container painted with it would be a white
        // slab, and Material would put `onErrorContainer` text on top of it.
        errorContainer = card,
        onErrorContainer = textPrimary,
        onTertiaryContainer = textPrimary,
        // The two extreme surface rungs. Material uses them for elevation extremes; they take the
        // nearest rungs this palette already declares rather than inventing two more greys.
        surfaceBright = graphite,
        surfaceDim = background,
    )

    companion object {
        /**
         * The one palette.
         *
         * THE GREYS ARE NEUTRAL, WHICH THEY WERE NOT. `graphite` was `#262223` -- R38 G34 B35, a
         * warm near-black with a red-brown cast that the SDS carries as a brand constant; and
         * `text.secondary`/`text.tertiary` were Zinc `#A1A1AA`/`#71717A`, which are blue. None of
         * the three is a grey. In a palette that has hue in exactly one place, three off-axis
         * near-greys are three quiet contradictions of the whole idea, and the one that showed was
         * `graphite`: it sat under avatar initials until a contrast fix moved them off it.
         *
         * THE RAMP IS SHORTER AND DARKER than the SDS's. `background` #0B0B0B -> #0A0A0A and
         * `surface` #171717 -> #141414 pull the chrome closer to the canvas, which is what makes a
         * black app read as one surface with things ON it rather than as a stack of grey plates.
         * The steps that carry meaning are kept: `background.deep` and `background` still differ,
         * because the frame picks between them BY ROUTE -- see `Navigation.kt` -- and that 1.06:1
         * step is the only thing separating a pushed screen from a tab.
         */
        val Dark = SwetgramColors(
            backgroundDeep = Color(0xFF000000),
            background = Color(0xFF0A0A0A),
            surface = Color(0xFF141414),
            card = Color(0xFF1F1F1F),
            graphite = Color(0xFF242424),
            border = Color(0xFF2A2A2A),
            borderStrong = Color(0xFF3D3D3D),
            textPrimary = Color(0xFFFFFFFF),
            textSecondary = Color(0xFFA3A3A3),
            textTertiary = Color(0xFF737373),
            textInverse = Color(0xFF000000),
            // The emphasis fill. Black sits on it -- see `ChatColors.onAccent`.
            accent = Color(0xFFFFFFFF),
            // The press state. A white fill cannot get brighter, so it steps DOWN; #D4D4D4 is
            // the same perceptual distance from white that `accentPressed` used to be from the
            // old red, and a black label still clears AA on it at 14.17:1 -- measured by
            // `PaletteContrastTest.thePressedAccentStillCarriesTheOnAccentLabel`, which is where
            // the 13.1 this comment used to claim was found to be wrong.
            accentPressed = Color(0xFFD4D4D4),
            // Near-black on the white fill. See the property: three raw `Color.White` literals
            // pointed here before this token existed, and all three would now be invisible.
            onAccent = Color(0xFF000000),
            // Presence. See the property.
            success = Color(0xFFFFFFFF),
            // Destruction, monochrome by decision. See the property for why the token survives.
            danger = Color(0xFFFFFFFF),
            // Active likes stay monochrome; the filled heart shape carries the state.
            liked = Color(0xFFFFFFFF),
            isDark = true,
        )
    }
}

// -------------------------------------------------------------------------------------------
// Contrast, computed rather than eyeballed
// -------------------------------------------------------------------------------------------

/**
 * WCAG 2.1 relative luminance of an sRGB colour.
 *
 * This lives in `commonMain` rather than in a test because a component has to be able to ASK.
 * `SwetAvatar` picks its own foreground from whichever background the hash landed on, and the
 * whole reason that fix exists is that a palette entry drifted off the neutral scale and nothing
 * noticed: a rule that only the test suite can evaluate is a rule the next palette edit walks
 * straight past. Pure Kotlin -- `kotlin.math` only -- so it compiles for iOS as well as Android.
 *
 * ALPHA IS IGNORED, deliberately. Luminance is only defined for an opaque colour; a translucent
 * one has no luminance until it has been composited onto something. Callers that need the
 * translucent case must composite first (`Color.compositeOver`) and pass the result.
 */
fun swetRelativeLuminance(color: Color): Double {
    fun channel(value: Float): Double {
        val c = value.toDouble()
        return if (c <= 0.03928) c / 12.92 else ((c + 0.055) / 1.055).pow(2.4)
    }
    return 0.2126 * channel(color.red) +
        0.7152 * channel(color.green) +
        0.0722 * channel(color.blue)
}

/**
 * WCAG 2.1 contrast ratio, 1.0 (identical) to 21.0 (black on white).
 *
 * AA wants 4.5:1 for normal text, 3:1 for large text and for non-text UI (WCAG 1.4.11).
 * Order does not matter -- the brighter of the two is always the numerator.
 */
fun swetContrastRatio(a: Color, b: Color): Double {
    val la = swetRelativeLuminance(a)
    val lb = swetRelativeLuminance(b)
    return (max(la, lb) + 0.05) / (min(la, lb) + 0.05)
}

/**
 * Chat surfaces, derived from the tokens rather than authored separately.
 *
 * THIS SET IMPLEMENTS A DELIBERATE OVERRIDE OF SDS §12.4 AND §4.3, RECORDED IN DECISIONS.md §25.
 *
 * §12.4 puts own messages on `accent.blue` with white text and the timestamp INSIDE the bubble;
 * §4.3 reserves `#000000` for "deepest background only" and makes `#0B0B0B` the app background.
 * The user supplied a conversation mockup that contradicts all three and asked for the mockup: a
 * pure-black canvas, a near-white own bubble with dark text, and the timestamp on the canvas beside
 * the bubble. The SDS is a reference document this repository does not own, so it is NOT edited to
 * match; the divergence is written down instead, here and in DECISIONS.md, so that anybody reading
 * this screen against §12.4 finds the answer rather than a discrepancy.
 *
 * WHAT THAT COST AND WHAT IT BOUGHT. The old pair -- white at 0.88 alpha on #3B82F6 -- measured
 * 3.20:1 for the timestamp and 3.7:1 for the body text, both short of AA for normal text, and both
 * were pinned as open design-system questions because the gap was the brand blue itself. The
 * mockup's pair closes them outright: #0B0B0B on #F2F2F2 is 17.6:1.
 *
 * THE METADATA HAS NO ROLE HERE, and its absence is deliberate rather than an omission. The
 * timestamp, the tick and "edited" are drawn INSIDE the bubble, so their colour is the bubble's own
 * foreground dimmed -- see `bubbleMetaColor` in `MessageBubble.kt`, which derives it per surface and
 * is held to AA by `BubbleMetaContrastTest`. A `meta` and a `readTick` role DID live here for one
 * commit, while the metadata sat on the canvas; when the user corrected that, nothing drew with
 * them and three tests went on measuring them against a surface they never touched. That is this
 * project's oldest failure -- a guard pointing at the room the thing used to be in -- so the roles
 * are gone rather than kept "in case".
 *
 * The read state is carried by the tick SHAPE (one check versus two), not by a tint: `MessageState`
 * has no DELIVERED rung, so the count IS the state (SDS §22.2). `MetaRow` takes one colour for the
 * timestamp and the tick together, which makes "the tick matches the timestamp" true by
 * construction instead of by assertion.
 *
 * ONE PAIR PER SURFACE AND NOTHING SPARE. Every colour the conversation draws is named here, because
 * a bubble colour written at three call sites is the drift the rest of this file exists to stop.
 */
data class ChatColors(
    /**
     * The conversation's background: the mockup's edge-to-edge black.
     *
     * `background.deep` rather than a literal, which makes it #000000 in dark and the ordinary app
     * background in light -- the light theme has no "deeper than background" and inventing a
     * near-white one below `#FAFAFA` would be a second background nobody specified.
     */
    val canvas: Color,
    val incomingBubble: Color,
    val onIncomingBubble: Color,
    /** The mockup's near-white own bubble. See [OutgoingBubbleDark] for the number. */
    val outgoingBubble: Color,
    val onOutgoingBubble: Color,
    /**
     * Whatever is drawn ON the accent fill: the send disc's arrow, the compose disc's pencil, the
     * "N new posts" label, the primary button's text.
     *
     * IT IS NEAR-BLACK NOW, AND THAT FLIP IS THE MOST DANGEROUS LINE IN THE RE-SKIN. It was white
     * for as long as the accent was a colour dark enough to sit under white -- first blue, then a
     * dark red. The accent is WHITE now, so white-on-accent is white-on-white: an arrow that
     * compiles, renders, and is simply not there. A single token, so there is one place to get it
     * right; `PaletteContrastTest.whateverIsDrawnOnTheAccentFillIsLegible` measures it.
     *
     * Nothing in the app may reach for a `Color.White` literal here. That literal is what made this
     * a one-line disaster waiting to happen.
     */
    val onAccent: Color,
    /**
     * The day separator, which is now text on the canvas and nothing else.
     *
     * There is no `daySeparator` fill any more: the mockup draws no pill, so a background token for
     * one would be a colour with no drawer. Deleted rather than left set to something plausible.
     */
    val daySeparatorText: Color,
) {
    companion object {
        /**
         * The mockup's own-message bubble in the dark theme.
         *
         * THE MOCKUP GAVE A RANGE, #EDEDED to #F2F2F2, AND THIS IS A CHOICE INSIDE IT rather than a
         * measurement. The light end was taken because everything about this bubble improves with
         * it: the dark text on it gains contrast, and so does the bubble's own edge against the
         * black canvas, which is the only thing separating an own message from the background.
         *
         * Deliberately NOT `text.primary` (#FFFFFF in dark). The mockup is specific that the bubble
         * is near-white and not white, and pure white on pure black is the one pairing that reads as
         * a rendering error rather than as a design.
         */
        val OutgoingBubbleDark: Color = Color(0xFFF2F2F2)

        fun from(colors: SwetgramColors) = ChatColors(
            canvas = colors.backgroundDeep,
            // #222222 in dark, which is inside the mockup's stated #1C1C1E-#242424 band, so the
            // incoming bubble needs no colour of its own. `SwetgramColorsTest` pins that it is
            // still `card` -- a bubble that quietly stopped being the card colour would put two
            // greys one tap apart on surfaces the SDS says are the same one.
            incomingBubble = colors.card,
            onIncomingBubble = colors.textPrimary,
            // The light theme is not the mockup's and cannot be: a near-white bubble on a near-white
            // canvas has no edge. What carries across is the RELATIONSHIP -- the own message is the
            // inverted, high-contrast one -- so light inverts the other way, and both themes then
            // read their text from `text.inverse` without a second branch.
            outgoingBubble = if (colors.isDark) OutgoingBubbleDark else colors.textPrimary,
            onOutgoingBubble = colors.textInverse,
            // The accent is white; what sits on it is not. See the property.
            onAccent = colors.textInverse,
            daySeparatorText = colors.textSecondary,
        )
    }
}
